import {memo} from 'react';
import {Can, Employee, WorkspaceBookType} from '@innowise-group/core';
import * as Styled from './workspace-view.styles';
import {redirectToHRMProfile} from '@innowise-group/utilities';
import {Icon, InformationTile, Divider} from '@innowise-group/ui-kit';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {subMonths} from 'date-fns';
import {filterDefaults, LS} from '@shared/constants';
import {proceedAttendanceBadgeVisibility} from '../../utilities';

interface WorkspaceBookingInfoProps {
  employee: Employee;
  range: {
    startDay: string;
    endDay: string;
  };
  comment?: string;
  status: WorkspaceBookType;
}

const WorkspaceBookingInfo = ({range, employee, comment, status}: WorkspaceBookingInfoProps) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const isAttendanceBadgeVisible = proceedAttendanceBadgeVisibility(employee?.visitationPercent, status);

  const handleUserProfileClick = () => {
    employee.hrm_id && redirectToHRMProfile(employee.hrm_id);
  };

  const handleBtnClick = () => {
    const today = new Date();

    const modifiedFilters = {
      ...filterDefaults,
      ...(employee.hrm_id && {hrmId: employee.hrm_id}),
      date_after: subMonths(today, 1),
      date_before: today,
    };

    localStorage.setItem(LS.StatusesFilters, JSON.stringify(modifiedFilters));

    const employeeName = employee?.preferredName || employee?.fullName;
    const searchParams = new URLSearchParams();

    if (employeeName) {
      searchParams.set('employee', employeeName);
    }

    const queryString = searchParams.toString();
    navigate(`/geotrack/statuses${queryString ? `?${queryString}` : ''}`);
  };

  return (
    <Styled.BookingInfoContainer>
      {range?.startDay && range?.endDay && (
        <Styled.BookingPeriodBlock>
          <Icon type="u_calender" size={16} />
          <Styled.BookingPeriodText>{`${range?.startDay} - ${range?.endDay}`}</Styled.BookingPeriodText>
        </Styled.BookingPeriodBlock>
      )}
      <Styled.WorkspaceEmployeeBlock>
        <Styled.UserPhoto onClick={handleUserProfileClick} isPointer={true} size={36} imageUrl={employee?.photoUrl} />
        <InformationTile
          title={employee?.fullName || t('pages.floors.withoutEmployee')}
          value={employee?.division || employee?.unit || employee?.department}
          isEmployeeAssigned={!!employee?.fullName}
        />
      </Styled.WorkspaceEmployeeBlock>
      {comment && (
        <>
          <Divider />
          <Styled.Tile title={`${t('modals.comment')}:`} value={comment} />
        </>
      )}
      <Can I="READ" a="GEOTRACK">
        {employee.fullName && isAttendanceBadgeVisible && (
          <Styled.ViewAttendanceBtn variant="outlined" onClick={handleBtnClick}>
            {t('buttons.viewAttendance')}
          </Styled.ViewAttendanceBtn>
        )}
      </Can>
    </Styled.BookingInfoContainer>
  );
};
export default memo(WorkspaceBookingInfo);
